import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Radio,
  RadioGroup,
  FormControlLabel,
  CircularProgress,
} from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import CloseIcon from '@mui/icons-material/Close';
import { getAddressSuggestions } from '../api/addressApi/addressApi';

const useStyles = makeStyles()((theme) => ({
  popupContainer: {
    '& .MuiDialog-paper': {
      backgroundColor: theme.palette.primary.light,
      color: theme.palette.common.white,
      minHeight: 'auto',
      padding: '32px',
      borderRadius: '16px',
      minWidth: '550px',
      maxWidth: '600px',
    },
  },
  popupTitle: {
    fontFamily: 'Poppins',
    fontWeight: 600,
    fontSize: '24px',
    lineHeight: '28.8px',
    padding: 0,
    marginBottom: '24px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  closeButton: {
    color: theme.palette.common.white,
    cursor: 'pointer',
    '&:hover': {
      opacity: 0.7,
    },
  },
  popupContent: {
    padding: 0,
    marginBottom: '24px',
  },
  addressSection: {
    marginBottom: '24px',
  },
  sectionLabel: {
    fontFamily: 'Inter',
    fontWeight: 500,
    fontSize: '14px',
    color: theme.palette.text.disabled,
    marginBottom: '8px',
  },
  addressDisplay: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontFamily: 'Inter',
    fontSize: '14px',
    color: theme.palette.common.white,
  },
  addressOption: {
    margin: 0,
    width: '100%',
    marginBottom: '16px',
    '& .MuiFormControlLabel-label': {
      width: '100%',
    },
    '& .MuiRadio-root': {
      color: theme.palette.secondary.dark,
      '&.Mui-checked': {
        color: theme.palette.info.main,
      },
    },
  },
  locationIcon: {
    color: theme.palette.info.main,
    fontSize: '16px',
  },
  vendorsSection: {
    marginBottom: '16px',
  },
  sectionTitle: {
    fontFamily: 'Inter',
    fontWeight: 600,
    fontSize: '16px',
    color: theme.palette.common.white,
    marginBottom: '16px',
  },
  radioGroup: {
    maxWidth: '500px',
    gap: '12px',
  },
  scrollableContainer: {
    maxHeight: '300px',
    overflowY: 'auto',
    overflowX: 'hidden',
    paddingRight: '8px',
    '&::-webkit-scrollbar': {
      width: '6px',
    },
    '&::-webkit-scrollbar-track': {
      backgroundColor: 'rgba(255, 255, 255, 0.1)',
      borderRadius: '3px',
    },
    '&::-webkit-scrollbar-thumb': {
      backgroundColor: 'rgba(255, 255, 255, 0.3)',
      borderRadius: '3px',
      '&:hover': {
        backgroundColor: 'rgba(255, 255, 255, 0.5)',
      },
    },
  },
  vendorOption: {
    margin: 0,
    width: '100%',
    '& .MuiFormControlLabel-label': {
      width: '100%',
    },
    '& .MuiRadio-root': {
      color: theme.palette.secondary.dark,
      '&.Mui-checked': {
        color: theme.palette.info.main,
      },
    },
  },
  vendorCard: {
    backgroundColor: 'rgba(255, 255, 255, 0.05)',
    border: '1px solid rgba(255, 255, 255, 0.1)',
    borderRadius: '8px',
    padding: '13px',
    width: '100%',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    '&:hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.08)',
    },
  },
  vendorCardSelected: {
    border: `1px solid ${theme.palette.info.main}`,
    backgroundColor: 'rgba(45, 212, 191, 0.1)',
  },
  vendorLabel: {
    fontFamily: 'Inter',
    fontSize: '12px',
    fontWeight: 500,
    color: theme.palette.text.disabled,
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    marginBottom: '4px',
  },
  vendorAddress: {
    fontFamily: 'Inter',
    fontSize: '14px',
    color: theme.palette.common.white,
    lineHeight: '20px',
  },
  popupActions: {
    display: 'flex',
    justifyContent: 'center',
    gap: '16px',
    padding: 0,
  },
  button: {
    height: '48px',
    borderRadius: '12px',
    fontFamily: 'Inter',
    fontWeight: 500,
    fontSize: '16px',
    textTransform: 'none',
    minWidth: '120px',
  },
  submitButton: {
    backgroundColor: theme.palette.info.main,
    color: theme.palette.common.white,
    '&:hover': {
      backgroundColor: theme.palette.info.main,
      opacity: 0.9,
    },
    '&:disabled': {
      backgroundColor: 'rgba(255, 255, 255, 0.08)',
      color: 'rgba(255, 255, 255, 0.3)',
    },
  },
  cancelButton: {
    backgroundColor: theme.palette.info.dark,
    color: theme.palette.common.white,
    '&:hover': {
      backgroundColor: theme.palette.info.dark,
      opacity: 0.9,
    },
  },
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '16px',
    padding: '40px 20px',
  },
  loadingText: {
    fontFamily: 'Inter',
    fontSize: '14px',
    color: theme.palette.text.disabled,
  },
}));

export interface VendorOption {
  id: string;
  placeId: string;
  label: string;
  address: string;
  addressLine1?: string;
  addressLine2?: string;
  city?: string;
  state?: string;
  zipcode?: string;
  accounting?: {
    isSynced: boolean;
    externalVendorId: string | null;
  };
}

interface VendorConfirmationPopupProps {
  open: boolean;
  title?: string;
  enteredAddress?: string;
  vendorOptions?: VendorOption[];
  confirmText?: string;
  cancelText?: string;
  onConfirm: (selectedVendor?: VendorOption, keepAsEntered?: boolean) => void;
  onCancel: () => void;
  onVendorSelect?: (vendor: VendorOption) => void;
  loading?: boolean;
  addressType?: string;
}

interface Prediction {
  description: string;
  placeId: string;
  structuredFormatting?: {
    mainText?: string;
    secondaryText?: string;
  };
}

const VendorConfirmationPopup: React.FC<VendorConfirmationPopupProps> = ({
  open,
  title = 'Confirm Address',
  enteredAddress = '',
  vendorOptions = [],
  confirmText = 'Select',
  cancelText = 'Cancel',
  onConfirm,
  onCancel,
  onVendorSelect,
  loading = false,
  addressType = 'Vendor Address',
}) => {
  const { classes } = useStyles();
  const [selectedVendorId, setSelectedVendorId] = useState<string>('');
  const [apiVendorOptions, setApiVendorOptions] = useState<VendorOption[]>([]);
  const [isLoadingVendors, setIsLoadingVendors] = useState<boolean>(false);

  // Reset selection when popup opens
  useEffect(() => {
    if (open) {
      setSelectedVendorId('');
      setApiVendorOptions([]);
    }
  }, [open]);

  // Fetch vendor suggestions when popup opens and enteredAddress is provided
  useEffect(() => {
    if (open && enteredAddress) {
      setIsLoadingVendors(true);
      getAddressSuggestions(enteredAddress)
        .then((response) => {
          if (
            response.data &&
            response.data.predictions &&
            Array.isArray(response.data.predictions)
          ) {
            // Map predictions to VendorOption format
            const mappedVendors: VendorOption[] = response.data.predictions.map(
              (prediction: Prediction, index: number) => ({
                id: `vendor-${index}$-{prediction.placeId}`,
                placeId: prediction.placeId,
                label: addressType,
                address: prediction.description,
                addressLine1: prediction.description,
              }),
            );

            setApiVendorOptions(mappedVendors);
          } else {
            setApiVendorOptions([]);
          }
        })
        .catch((error) => {
          console.error('Error fetching address suggestions:', error);
          setApiVendorOptions([]);
        })
        .finally(() => {
          setIsLoadingVendors(false);
        });
    }
  }, [open, enteredAddress, addressType]);

  // Use API vendor options if available, otherwise use passed vendorOptions
  const displayVendorOptions = apiVendorOptions.length > 0 ? apiVendorOptions : vendorOptions;

  const handleVendorChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const vendorId = event.target.value;
    setSelectedVendorId(vendorId);
    // Don't call onVendorSelect here - wait for user to confirm selection
  };

  const handleConfirm = () => {
    if (selectedVendorId === 'entered_address') {
      // User selected to keep their entered address
      onConfirm(undefined, true); // Pass keepAsEntered = true
    } else {
      // User selected a suggested vendor
      const selectedVendor = displayVendorOptions.find((vendor) => vendor.id === selectedVendorId);

      // Call onVendorSelect only when user confirms the selection
      if (selectedVendor && onVendorSelect) {
        onVendorSelect(selectedVendor);
      }

      onConfirm(selectedVendor, false); // Pass keepAsEntered = false
    }
  };

  const handleClose = () => {
    setSelectedVendorId('');
    onCancel();
  };

  return (
    <Dialog open={open} onClose={handleClose} className={classes.popupContainer} maxWidth={false}>
      <DialogTitle className={classes.popupTitle}>
        {title}
        <CloseIcon className={classes.closeButton} onClick={handleClose} />
      </DialogTitle>

      <DialogContent className={classes.popupContent}>
        {loading || isLoadingVendors ? (
          <Box className={classes.loadingContainer}>
            <CircularProgress size={32} sx={{ color: 'info.main' }} />
            <Typography className={classes.loadingText}>Finding matching vendors...</Typography>
          </Box>
        ) : (
          <>
            {/* Entered Address Section */}
            {enteredAddress && (
              <Box className={classes.addressSection}>
                <Typography className={classes.sectionLabel}>Keep as entered</Typography>
                <RadioGroup
                  value={selectedVendorId}
                  onChange={handleVendorChange}
                  className={classes.radioGroup}
                >
                  {/* Entered Address as Radio Option */}
                  <FormControlLabel
                    value="entered_address"
                    control={<Radio />}
                    label={
                      <Box
                        className={`${classes.vendorCard} ${
                          selectedVendorId === 'entered_address' ? classes.vendorCardSelected : ''
                        }`}
                      >
                        <Typography className={classes.vendorLabel}>{addressType}</Typography>
                        <Typography className={classes.vendorAddress}>{enteredAddress}</Typography>
                      </Box>
                    }
                    className={classes.addressOption}
                  />
                </RadioGroup>
              </Box>
            )}

            {/* Suggested Vendors Section */}
            <Box className={classes.vendorsSection}>
              <Typography className={classes.sectionTitle}>Suggested Addresses</Typography>

              {displayVendorOptions.length > 0 ? (
                <Box className={classes.scrollableContainer}>
                  <RadioGroup
                    value={selectedVendorId}
                    onChange={handleVendorChange}
                    className={classes.radioGroup}
                  >
                    {displayVendorOptions.map((vendor) => (
                      <FormControlLabel
                        key={vendor.id}
                        value={vendor.id}
                        control={<Radio />}
                        label={
                          <Box
                            className={`${classes.vendorCard} ${
                              selectedVendorId === vendor.id ? classes.vendorCardSelected : ''
                            }`}
                          >
                            <Typography className={classes.vendorLabel}>{vendor.label}</Typography>
                            <Typography className={classes.vendorAddress}>
                              {vendor.address}
                            </Typography>
                          </Box>
                        }
                        className={classes.vendorOption}
                      />
                    ))}
                  </RadioGroup>
                </Box>
              ) : (
                <Typography
                  sx={{
                    color: 'text.disabled',
                    fontSize: '14px',
                    textAlign: 'center',
                    padding: '20px',
                  }}
                >
                  No vendor suggestions found for this address
                </Typography>
              )}
            </Box>
          </>
        )}
      </DialogContent>

      <DialogActions className={classes.popupActions}>
        <Button onClick={handleClose} className={`${classes.button} ${classes.cancelButton}`}>
          {cancelText}
        </Button>
        <Button
          onClick={handleConfirm}
          className={`${classes.button} ${classes.submitButton}`}
          disabled={!selectedVendorId || loading || isLoadingVendors}
        >
          {confirmText}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default VendorConfirmationPopup;
